<template>
  <div>
    <section id="filters">
      <vs-divider position="left">Where</vs-divider>
      <spatial-filter :force-active="true" />
      <vs-divider position="left">Sector</vs-divider>
      <div class="sectors-container">
        <app-radio-list
          class="sectors-radio"
          :items="sectorsList"
          @change="updateActiveSector"
        />
        <img
          :src="
            activeSector.name === sectors.aquaculture.name
              ? require(`@/assets/menus/aquaculture_dark.png`)
              : require(`@/assets/menus/marine_dark.png`)
          "
          class="sector-ico"
        />
      </div>
    </section>

    <section v-show="buoys.length > 0" id="features-section">
      <vs-divider position="left">Feature</vs-divider>
      <div class="section-container">
        <vs-select id="cmbBuoys" v-model="activeBuoy">
          <vs-select-item
            v-for="(buoy, index) in buoys"
            :key="index"
            :value="buoy"
            :text="buoy.name"
          />
        </vs-select>
        <div class="button">
          <vs-button
            type="filled"
            size="small"
            icon="dns"
            color="#19535F"
            @click="checkOperativity()"
            >Check Actual Operativity</vs-button
          >
        </div>
        <div v-if="isEnergyActive" class="button">
          <vs-button
            type="filled"
            size="small"
            icon="dns"
            color="#19535F"
            @click="checkPotentialEnergy()"
            >Check Potential Energy</vs-button
          >
        </div>
      </div>
    </section>
    <section>
      <div v-if="isAquacultureActive && showOperativityResults">
        <vs-divider>Operativity Conditions for Aquaculture</vs-divider>
        <div class="grid-results-aquaculture">
          <div class="header-item">
            Wave Height -
            <label class="date-label"
              >{{ operativityResults.aquaculture.waveHeight.date }} -</label
            >
          </div>
          <div class="result-item">
            <vs-chip>
              <vs-avatar
                :icon="aquaOPWaveHeightIcon"
                text
                class="index-avatar"
              />
              <span class="label-result"
                >{{ operativityResults.aquaculture.waveHeight.value }}m</span
              >
            </vs-chip>
            <span
              :style="{
                backgroundColor: aquaOPCategoryColor
              }"
              class="dot"
            />
          </div>
        </div>
      </div>
      <div v-if="isEnergyActive && showOperativityResults">
        <vs-divider>Operativity Conditions for Marine Energy</vs-divider>
        <div class="grid-results-energy">
          <div class="header-item">
            Wave Height -
            <label class="date-label"
              >{{ operativityResults.energy.waveHeight.date }} -</label
            >
          </div>
          <div class="header-item">
            Wind Speed -
            <label class="date-label"
              >{{ operativityResults.energy.windSpeed.date }} -</label
            >
          </div>
          <div class="result-item">
            <vs-chip>
              <vs-avatar
                :icon="energyOPWaveHeightIcon"
                text
                class="index-avatar"
              />
              <span class="label-result"
                >{{ operativityResults.energy.waveHeight.value }}m</span
              >
            </vs-chip>
            <span
              :style="{ backgroundColor: energyOPWaveHeightColor }"
              class="dot"
            />
          </div>
          <div class="result-item">
            <vs-chip>
              <vs-avatar
                :icon="energyOPWindSpeedIcon"
                text
                class="index-avatar"
              />
              <span class="label-result"
                >{{ operativityResults.energy.windSpeed.value }}m-s</span
              >
            </vs-chip>
            <span
              :style="{ backgroundColor: energyOPWindSpeedColor }"
              class="dot"
            />
          </div>
        </div>
      </div>
      <div v-if="isEnergyActive && showPotentialResults">
        <vs-divider>Potential Conditions for Marine Energy</vs-divider>
        <div class="grid-results-energy">
          <div class="header-item">
            Wave Potential -
            <label class="date-label"
              >{{ potentialEnergyResults.waves.date }} -</label
            >
          </div>
          <div class="header-item">
            Wind Potential -
            <label class="date-label"
              >{{ potentialEnergyResults.wind.date }} -</label
            >
          </div>
          <div class="result-item">
            <vs-chip>
              <span class="label-result"
                >{{ potentialEnergyResults.waves.value }} W/m</span
              >
            </vs-chip>
          </div>
          <div class="result-item">
            <vs-chip>
              <span class="label-result"
                >{{ potentialEnergyResults.wind.value }} W/m²</span
              >
            </vs-chip>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import SpatialFilterControl from "@/components/ui/SpatialFilterControl";
import AppRadioButtonList from "@/components/ui/AppRadioButtonList";
import {
  getBuoysFromPoint,
  getVariablesFromBuoy,
  getBuoyLastMeasure
} from "@/api/sensors";
import {
  CONFIG_OPERATIVITY_AQUA,
  CONFIG_OPERATIVITY_ENERGY
} from "@/config/bluegrowth-config.js";
import { SECTORS } from "@/constants.js";
import { mapState, mapActions } from "vuex";

export default {
  components: {
    "spatial-filter": SpatialFilterControl,
    "app-radio-list": AppRadioButtonList
  },
  props: {
    momentId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      activeSector: {},
      activeBuoy: {},
      energyValues: {},
      buoys: [],
      buoysAquaculture: [],
      buoysEnergy: [],
      categoryColors: {
        bad: "#a50026",
        good: "#1a9850"
      },
      operativityResults: {
        aquaculture: {
          waveHeight: { value: 0, date: null }
        },
        energy: {
          waveHeight: { value: 0, date: null },
          windSpeed: { value: 0, date: null }
        }
      },
      potentialEnergyResults: {
        waves: { value: 0, date: null },
        wind: { value: 0, date: null }
      },
      sectors: SECTORS,
      sectorsList: [],
      showOperativityResults: false,
      showPotentialResults: false
    };
  },
  computed: {
    ...mapState("map", {
      marker: state => state.marker
    }),
    isAquacultureActive() {
      return this.activeSector.name === this.sectors.aquaculture.name;
    },
    isEnergyActive() {
      return this.activeSector.name === this.sectors.energy.name;
    },
    aquaOPWaveHeightIcon() {
      return this.operativityResults.aquaculture.waveHeight.value >
        CONFIG_OPERATIVITY_AQUA.present.variables.waves.thresholds.max
        ? "thumb_down"
        : "thumb_up";
    },
    aquaOPCategoryColor() {
      return this.operativityResults.aquaculture.waveHeight.value >
        CONFIG_OPERATIVITY_AQUA.present.variables.waves.thresholds.max
        ? this.categoryColors.bad
        : this.categoryColors.good;
    },
    energyOPWaveHeightIcon() {
      return this.operativityResults.aquaculture.waveHeight.value >
        CONFIG_OPERATIVITY_ENERGY.present.variables.waves.thresholds.max
        ? "thumb_down"
        : "thumb_up";
    },
    energyOPWindSpeedIcon() {
      return this.operativityResults.aquaculture.waveHeight.value >
        CONFIG_OPERATIVITY_ENERGY.present.variables.wind.thresholds.max
        ? "thumb_down"
        : "thumb_up";
    },
    energyOPWaveHeightColor() {
      return this.operativityResults.energy.waveHeight.value >
        CONFIG_OPERATIVITY_ENERGY.present.variables.waves.thresholds.max
        ? this.categoryColors.bad
        : this.categoryColors.good;
    },
    energyOPWindSpeedColor() {
      return this.operativityResults.energy.windSpeed.value >
        CONFIG_OPERATIVITY_ENERGY.present.variables.wind.thresholds.max
        ? this.categoryColors.bad
        : this.categoryColors.good;
    }
  },
  watch: {
    marker() {
      this.searchBuoys(); //find closest buoys on marker change
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    ...mapActions({
      toggleLoading: "toggleLoading"
    }),
    init() {
      this.setSectorsList();
      this.searchBuoys();
    },
    searchBuoys() {
      this.toggleLoading(true);
      getBuoysFromPoint(this.marker)
        .then(async buoys => {
          await this.loadAquacultureBuoys(buoys);
          await this.loadEnergyBuoys(buoys);
          this.setBuoysList();
        })
        .catch(error => {
          this.$_utilsMixin_notify("Error", error, 2);
        })
        .finally(() => {
          this.toggleLoading(false);
        });
    },
    async loadAquacultureBuoys(buoys) {
      /* Needed: Wave height, (and maybe Currents)*/
      const validBuoys = [];
      for (const buoy of buoys) {
        const variables = await getVariablesFromBuoy(buoy.id);
        const varNames = variables.map(v => v.name);
        if (
          varNames.includes(
            CONFIG_OPERATIVITY_AQUA.present.variables.waves.name
          )
        ) {
          validBuoys.push(buoy);
        }
      }
      this.buoysAquaculture = validBuoys;
    },
    async loadEnergyBuoys(buoys) {
      /* Needed: Wave height , Wind */
      const validBuoys = [];
      for (const buoy of buoys) {
        const variables = await getVariablesFromBuoy(buoy.id);
        const varNames = variables.map(v => v.name);
        if (
          varNames.includes(
            CONFIG_OPERATIVITY_ENERGY.present.variables.waves.name
          ) &&
          varNames.includes(
            CONFIG_OPERATIVITY_ENERGY.present.variables.wind.name
          )
        ) {
          validBuoys.push(buoy);
        }
      }
      this.buoysEnergy = validBuoys;
    },
    setSectorsList() {
      for (const sectorKey of Object.keys(this.sectors)) {
        this.sectorsList.push(this.sectors[sectorKey]);
      }
    },
    setBuoysList() {
      this.buoys = this.isAquacultureActive
        ? this.buoysAquaculture
        : this.buoysEnergy;
      this.activeBuoy = this.buoys[0];
    },
    checkOperativity() {
      this.calculateOperativity();
    },
    async calculateOperativity() {
      try {
        this.toggleLoading(true);
        const measure = await getBuoyLastMeasure(this.activeBuoy.id);
        if (this.isAquacultureActive) {
          this.setAquaOperativityResults(measure);
        } else {
          this.setEnergyOperativityResults(measure);
        }
        this.showOperativityResults = true;
      } catch (error) {
        this.$_utilsMixin_notify("Error calculating operativity", error, 2);
      } finally {
        this.toggleLoading(false);
      }
    },
    checkPotentialEnergy() {
      this.calculatePotentialEnergy();
    },
    async calculatePotentialEnergy() {
      try {
        this.toggleLoading(true);
        const measure = await getBuoyLastMeasure(this.activeBuoy.id);
        this.setPotentialEnergyResults(measure);
        this.showPotentialResults = true;
      } catch (error) {
        this.$_utilsMixin_notify(
          "Error calculating potential energy",
          error,
          2
        );
      } finally {
        this.toggleLoading(false);
      }
    },
    setAquaOperativityResults(measure) {
      const waveHeight = measure.dataCustom.find(m => {
        return (
          m.variable === CONFIG_OPERATIVITY_AQUA.present.variables.waves.name
        );
      });
      const roundedWaveHeight = waveHeight.value.toFixed(2);
      this.operativityResults.aquaculture.waveHeight.value = roundedWaveHeight;
      this.operativityResults.aquaculture.waveHeight.date = this.parseDate(
        waveHeight.date
      );
    },
    setEnergyOperativityResults(measure) {
      const waveHeight = measure.dataCustom.find(m => {
        return (
          m.variable === CONFIG_OPERATIVITY_ENERGY.present.variables.waves.name
        );
      });
      const windSpeed = measure.dataCustom.find(m => {
        return (
          m.variable === CONFIG_OPERATIVITY_ENERGY.present.variables.wind.name
        );
      });
      this.operativityResults.energy.waveHeight.value = waveHeight.value.toFixed(
        2
      );
      this.operativityResults.energy.waveHeight.date = this.parseDate(
        waveHeight.date
      );
      this.operativityResults.energy.windSpeed.value = windSpeed.value.toFixed(
        2
      );
      this.operativityResults.energy.windSpeed.date = this.parseDate(
        windSpeed.date
      );
    },
    setPotentialEnergyResults(measure) {
      this.setPotentialWavesResult(measure);
      this.setPotentialWindResult(measure);
    },
    setPotentialWavesResult(measure) {
      const waveHeight = measure.dataCustom.find(m => {
        return (
          m.variable === CONFIG_OPERATIVITY_ENERGY.present.variables.waves.name
        );
      });
      const tp = measure.dataCustom.find(m => {
        return (
          m.variable === CONFIG_OPERATIVITY_ENERGY.present.variables.tp.name
        );
      });
      const pWaves = 570 * Math.pow(waveHeight.value, 2) * tp.value;
      this.potentialEnergyResults.waves.date = this.parseDate(waveHeight.date);
      this.potentialEnergyResults.waves.value = pWaves.toFixed(2);
    },
    setPotentialWindResult(measure) {
      const windSpeed = measure.dataCustom.find(m => {
        return (
          m.variable === CONFIG_OPERATIVITY_ENERGY.present.variables.wind.name
        );
      });
      const pWind = 0.5 * 1.29 * Math.pow(windSpeed.value, 3);
      this.potentialEnergyResults.wind.date = this.parseDate(windSpeed.date);
      this.potentialEnergyResults.wind.value = pWind.toFixed(2);
    },
    parseDate(dateString) {
      const dateObj = new Date(dateString);
      return dateObj.toLocaleDateString("en-EN", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric"
      });
    },
    updateActiveSector(sector) {
      this.activeSector = sector;
      this.setBuoysList();
      this.showOperativityResults = false;
      this.showPotentialResults = false;
    },
    updateActiveBuoy(buoy) {
      this.activeBuoy = buoy;
    }
  }
};
</script>
<style scoped>
.grid-results-aquaculture {
  display: grid;
  grid-template-columns: 1fr;
  /* grid-template-rows: 9rem 1fr 0.4fr 1rem; */
  grid-template-rows: 20px 1fr;
  grid-gap: 1rem;
  margin-bottom: 2rem;
  margin-top: 2rem;
}

.grid-results-energy {
  display: grid;
  grid-template-columns: 1fr 1fr;
  /* grid-template-rows: 9rem 1fr 0.4fr 1rem; */
  grid-template-rows: 20px 1fr;
  grid-gap: 1rem;
  margin-bottom: 2rem;
  margin-top: 2rem;
}

.header-item {
  font-size: 1rem;
  font-weight: bold;
}

.result-item {
  display: flex;
  flex-grow: 1;
  flex-direction: row;
  justify-content: center;
}

.date-label {
  font-size: 0.8rem;
}

.button {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
  margin-right: 1rem;
}
.sectors-container {
  display: flex;
  margin-right: 1rem;
}
.sectors-radio >>> li {
  padding: 0px !important;
}

.sector-ico {
  width: 50px;
  height: 50px;
}
</style>
